import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { API, axios } from '../utils/api';
import { aggiungiGiorni, daIso, isoGiorno, MESI, testoAggiornata } from '../utils/agenda';
import { adessoRoma, disponibilitaGiornoDiRoma } from '../utils/percorso';
import { useTraduzioni, contesto, PRINCIPALE } from '../i18n';

// Disponibilità del cliente per le visite (backend/disponibilita.py): 7 giorni × mattina / pomeriggio / sera,
// "vale ogni settimana" o una griglia per settimana, eccezioni per data. Si salva a ogni tocco. La usano il
// calendario e il profilo del cliente e la scheda cliente dello staff (Panoramica e Visite): una sola fonte, chi salva
// per ultimo vince; sotto, chi l'ha aggiornata e quando. Le visite già fissate occupano il loro spazio.
// I salvataggi partono uno alla volta, sempre con l'ultimo stato: con la rete lenta nessun tocco si perde (prima
// partivano tutti insieme e vinceva l'ultima risposta arrivata, non l'ultimo tocco).
// Un giorno con un'eccezione si tocca per quel giorno soltanto ("quel giorno no" → "quel giorno solo la sera").

const FASCE = ['mattina', 'pomeriggio', 'sera'];
const GIORNI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
// I giorni e i mesi nella lingua di chi guarda (lo staff resta in italiano: la lingua di base)
const giornoBreve = (i, lingua) => (lingua === PRINCIPALE ? GIORNI[i]
  : contesto(lingua).data(daIso(aggiungiGiorni('2024-01-01', i)), { weekday: 'short', timeZone: undefined })); // 1/1/2024 è un lunedì
const giornoIntero = (i, lingua) => (lingua === PRINCIPALE ? GIORNI_INTERI[i]
  : contesto(lingua).data(daIso(aggiungiGiorni('2024-01-01', i)), { weekday: 'long', timeZone: undefined }));
const mese = (d, lingua) => (lingua === PRINCIPALE ? MESI[d.getMonth()]
  : contesto(lingua).data(d, { month: 'long', timeZone: undefined }));
const meseBreve = (d, lingua) => (lingua === PRINCIPALE ? MESI[d.getMonth()].slice(0, 3)
  : contesto(lingua).data(d, { month: 'short', timeZone: undefined }));

export const lunediDi = (iso) => {
  const d = daIso(iso);
  return aggiungiGiorni(iso, -((d.getDay() + 6) % 7));
};

export const intervalloSettimana = (lunedi, lingua = PRINCIPALE) => {
  const a = daIso(lunedi);
  const b = daIso(aggiungiGiorni(lunedi, 6));
  // fuori dall'italiano il mese da solo sarebbe al nominativo ("вересень"): si scrive giorno e mese insieme
  const giornoEMese = (d) => (lingua === PRINCIPALE ? `${d.getDate()} ${mese(d, lingua)}`
    : contesto(lingua).data(d, { day: 'numeric', month: 'long', timeZone: undefined }));
  return a.getMonth() === b.getMonth()
    ? `${a.getDate()} – ${giornoEMese(b)}`
    : `${giornoEMese(a)} – ${giornoEMese(b)}`;
};

// Le fasce libere di un giorno (stessa regola del backend: le eccezioni vincono)
export const fasceLibere = (disp, iso) => {
  if (!disp) return [];
  if (disp.eccezioni && iso in disp.eccezioni) return disp.eccezioni[iso];
  const griglia = disp.ogni_settimana !== false ? disp.settimana : (disp.settimane || {})[lunediDi(iso)];
  return (griglia || {})[String((daIso(iso).getDay() + 6) % 7)] || [];
};

const OGNI_MEZZO_MINUTO = 30 * 1000;

const GIORNI_INTERI = ['lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato', 'domenica'];

// onCaricata: una volta, quando la griglia arriva e si disegna (il calendario, aperto su #disponibilita, ci torna
// sopra: prima di allora la sezione è una riga sola e la pagina può non avere ancora dove scorrere)
// rileggi: un numero che cambia quando la pagina sa che le visite sono cambiate (il calendario, dopo "Abbiamo aggiornato
// il calendario" per una visita annullata): la griglia si rilegge, prima restava la cella "visita già fissata"
// titolo e spiegazione: di base quelli del cliente, nella sua lingua (la scheda dello staff passa i suoi)
const Disponibilita = ({ clienteId, titolo, spiegazione, dello_staff = false, onCambia, onCaricata, rileggi = 0 }) => {
  const { t, l } = useTraduzioni();
  const nomeFascia = (f) => t(`calendario.fasce.${f}`);
  const intestazione = titolo === undefined ? t('calendario.disp.titolo') : titolo;
  const sottotitolo = spiegazione === undefined ? t('calendario.disp.spiegazione') : spiegazione;
  const [stato, setStato] = useState(null);
  const [errore, setErrore] = useState(false);
  const [settimana, setSettimana] = useState(0);
  const [nuovaEccezione, setNuovaEccezione] = useState('');
  const daSalvare = useRef(null); // l'ultimo stato toccato, che aspetta di partire
  const inVolo = useRef(false);
  const vivo = useRef(true);
  // La versione letta (aggiornata_alle del server), mandata con ogni salvataggio: se nel frattempo un'altra pagina o
  // un'altra persona l'ha cambiata il server risponde 409 e si rilegge (prima una griglia rimasta aperta rimandava la
  // sua copia vecchia e cancellava quella nuova). Si aggiorna a ogni risposta, anche quando non si mostra.
  const versione = useRef(undefined);
  const salvataggi = useRef(0); // una lettura partita prima di un salvataggio non rimette la versione vecchia
  useEffect(() => () => { vivo.current = false; }, []);

  const leggi = useCallback(() => {
    setErrore(false);
    const giro = salvataggi.current;
    axios.get(`${API}/clienti/${clienteId}/disponibilita`)
      .then((r) => {
        if (!vivo.current || inVolo.current || giro !== salvataggi.current) return;
        versione.current = r.data?.disponibilita?.aggiornata_alle ?? null;
        setStato(r.data);
      })
      .catch(() => { if (vivo.current) setErrore(true); });
  }, [clienteId]);
  useEffect(() => { leggi(); }, [leggi]);
  useEffect(() => { if (rileggi && !inVolo.current) leggi(); }, [rileggi, leggi]);
  const avvisata = useRef(false);
  useEffect(() => {
    if (!stato?.disponibilita || avvisata.current) return;
    avvisata.current = true;
    if (onCaricata) onCaricata();
  }, [stato, onCaricata]);
  // "Oggi" dall'orologio (ora di Roma), non solo dal giorno letto all'apertura: lasciata aperta oltre la mezzanotte
  // la griglia lasciava toccare il giorno ormai passato. Si tiene il più avanti dei due (orologio del telefono
  // indietro); al cambio di giorno si rilegge (le eccezioni passate spariscono, le visite occupate si aggiornano).
  const [oggiOrologio, setOggiOrologio] = useState(() => isoGiorno(adessoRoma()));
  useEffect(() => {
    const t = setInterval(() => setOggiOrologio(isoGiorno(adessoRoma())), OGNI_MEZZO_MINUTO);
    return () => clearInterval(t);
  }, []);
  const oggiDiPrima = useRef(oggiOrologio);
  useEffect(() => {
    if (oggiDiPrima.current === oggiOrologio) return;
    oggiDiPrima.current = oggiOrologio;
    if (!inVolo.current) leggi();
  }, [oggiOrologio, leggi]);
  const oggi = stato?.oggi && stato.oggi > oggiOrologio ? stato.oggi : oggiOrologio;
  const lunedi = aggiungiGiorni(lunediDi(oggi), settimana * 7);
  const disp = stato?.disponibilita;
  const occupati = useMemo(() => new Set((stato?.occupati || []).map((o) => `${o.data}|${o.fascia}`)), [stato]);

  // Coda di un posto: parte un salvataggio alla volta; i tocchi arrivati nel frattempo partono dopo, tutti insieme
  // (l'ultimo stato). La risposta del server si usa solo se nel frattempo non ci sono stati altri tocchi.
  const svuota = async () => {
    inVolo.current = true;
    let rileggere = false;
    while (daSalvare.current) {
      const dati = daSalvare.current;
      daSalvare.current = null;
      salvataggi.current += 1;
      try {
        // senza la versione letta (undefined: pagina ancora senza lettura) si salva come prima
        const corpo = versione.current === undefined ? dati : { ...dati, versione: versione.current };
        const r = await axios.put(`${API}/clienti/${clienteId}/disponibilita`, corpo);
        versione.current = r.data?.disponibilita?.aggiornata_alle ?? null;
        rileggere = false;
        if (!daSalvare.current && vivo.current) {
          setStato(r.data);
          if (onCambia) onCambia(r.data);
        }
      } catch (e) {
        if (e?.response?.status === 409) {
          // cambiata nel frattempo (un'altra scheda, lo staff, il cliente): i tocchi fatti sulla copia vecchia non
          // partono, si mostra quella salvata e lo si dice
          daSalvare.current = null;
          toast.error(t(dello_staff ? 'calendario.disp.cambiata_staff' : 'calendario.disp.cambiata'));
        } else {
          toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : t('calendario.disp.non_salvata'));
        }
        rileggere = !daSalvare.current; // si torna a quello che è davvero salvato
      }
    }
    inVolo.current = false;
    if (rileggere && vivo.current) leggi();
  };
  const salva = (nuova) => {
    setStato((s) => ({ ...s, disponibilita: nuova }));
    daSalvare.current = nuova;
    if (!inVolo.current) svuota();
  };

  if (errore && !stato) {
    return (
      <div data-testid="disponibilita" role="alert">
        {intestazione && <div className="ttl"><h2>{intestazione}</h2></div>}
        <p className="muto" style={{ fontSize: '.86rem', margin: '.2rem 0 .7rem' }}>{t('calendario.disp.errore')}</p>
        <button type="button" className="btn sm" onClick={leggi}>{t('comune.riprova')}</button>
      </div>
    );
  }
  if (!stato?.disponibilita) return <p className="muto" style={{ fontSize: '.82rem' }} data-testid="disponibilita-attesa">{t('calendario.disp.carico')}</p>;

  const sempre = disp.ogni_settimana !== false;
  const griglia = sempre ? (disp.settimana || {}) : ((disp.settimane || {})[lunedi] || {});
  const eccezioniTutte = disp.eccezioni || {};
  const tocca = (g, fascia) => {
    const giorno = aggiungiGiorni(lunedi, g);
    if (occupati.has(`${giorno}|${fascia}`)) {
      // prima il tocco non faceva niente, senza dire perché; si rilegge anche la griglia (la visita può essere stata
      // annullata o spostata nel frattempo: allora la cella si libera)
      const quando = `${giornoIntero(g, l)} ${daIso(giorno).getDate()} ${nomeFascia(fascia).toLowerCase()}`;
      toast(t(dello_staff ? 'calendario.disp.occupato_staff' : 'calendario.disp.occupato',
        { quando: `${quando.charAt(0).toUpperCase()}${quando.slice(1)}` }));
      if (!inVolo.current) leggi();
      return;
    }
    if (giorno in eccezioniTutte) {
      // un giorno con l'eccezione: si cambia quel giorno soltanto, non la griglia di tutte le settimane
      const libere = eccezioniTutte[giorno] || [];
      const nuove = libere.includes(fascia) ? libere.filter((f) => f !== fascia) : FASCE.filter((f) => f === fascia || libere.includes(f));
      salva({ ...disp, eccezioni: { ...eccezioniTutte, [giorno]: nuove } });
      return;
    }
    const attuali = griglia[String(g)] || [];
    const nuove = attuali.includes(fascia) ? attuali.filter((f) => f !== fascia) : [...attuali, fascia];
    const nuovaGriglia = { ...griglia, [String(g)]: nuove };
    if (sempre) salva({ ...disp, settimana: nuovaGriglia });
    else salva({ ...disp, settimane: { ...(disp.settimane || {}), [lunedi]: nuovaGriglia } });
  };
  const ogniSettimana = () => {
    if (!sempre) {
      salva({ ...disp, ogni_settimana: true });
      return;
    }
    // Spento: la griglia tipo si copia in questa settimana e nella prossima (se non ne hanno già una loro), così
    // non si svuota niente di quello che si vede
    const questa = lunediDi(oggi);
    const settimane = { ...(disp.settimane || {}) };
    [questa, aggiungiGiorni(questa, 7)].forEach((l) => { if (!settimane[l]) settimane[l] = { ...(disp.settimana || {}) }; });
    salva({ ...disp, ogni_settimana: false, settimane });
  };
  const aggiungiEccezione = () => {
    if (!nuovaEccezione) return;
    if (nuovaEccezione < oggi) {
      toast.error(t('calendario.disp.scegliete_da_oggi'));
      return;
    }
    salva({ ...disp, eccezioni: { ...eccezioniTutte, [nuovaEccezione]: [] } });
    setNuovaEccezione('');
  };
  const togliEccezione = (giorno) => {
    const eccezioni = { ...eccezioniTutte };
    delete eccezioni[giorno];
    salva({ ...disp, eccezioni });
  };
  const eccezioni = Object.keys(eccezioniTutte).filter((g) => g >= oggi).sort();
  const mai = GIORNI.map((_, g) => g).filter((g) => !(griglia[String(g)] || []).length).map((g) => giornoBreve(g, l));
  const testoEccezione = (g) => {
    const libere = eccezioniTutte[g] || [];
    return libere.length
      ? t('calendario.disp.solo', { fasce: libere.map((f) => nomeFascia(f).toLowerCase()).join(` ${t('comune.e')} `) })
      : t('calendario.disp.mai');
  };

  return (
    <div data-testid="disponibilita">
      {intestazione && <div className="ttl"><h2>{intestazione}</h2></div>}
      {sottotitolo && <p className="muto" style={{ fontSize: '.82rem', marginBottom: '.7rem' }}>{sottotitolo}</p>}
      <div className="dweek">
        <b>{intervalloSettimana(lunedi, l)}</b>
        <div className="seg">
          <button type="button" className={settimana === 0 ? 'on' : undefined} onClick={() => setSettimana(0)}>{t('calendario.disp.questa')}</button>
          <button type="button" className={settimana === 1 ? 'on' : undefined} onClick={() => setSettimana(1)}>{t('calendario.disp.prossima')}</button>
        </div>
      </div>
      {sempre && <p className="muto" style={{ fontSize: '.74rem', margin: '-.3rem 0 .5rem' }} data-testid="griglia-tipo">{t('calendario.disp.griglia_tipo')}</p>}
      <div className="disp" role="grid" aria-label={t('calendario.disp.griglia')}>
        <div />
        {GIORNI.map((g, i) => <div key={g} className={`lab${i >= 5 ? ' we' : ''}`}>{giornoBreve(i, l)}</div>)}
        {FASCE.map((fascia) => (
          <React.Fragment key={fascia}>
            <div className="fl">{nomeFascia(fascia)}</div>
            {GIORNI.map((g, i) => {
              const giorno = aggiungiGiorni(lunedi, i);
              const occ = occupati.has(`${giorno}|${fascia}`);
              const ecc = giorno in eccezioniTutte;
              const on = ecc ? (eccezioniTutte[giorno] || []).includes(fascia) : (griglia[String(i)] || []).includes(fascia);
              // i giorni passati contano solo nella griglia di una settimana precisa; la settimana tipo vale sempre
              const passato = giorno < oggi && (!sempre || ecc);
              const classi = ['c', on ? 'on' : '', occ ? 'occ' : '', ecc && !on ? 'no' : '', i >= 5 ? 'we' : ''].filter(Boolean).join(' ');
              return (
                <button type="button" key={g} className={classi} disabled={passato} onClick={() => tocca(i, fascia)}
                  aria-pressed={on}
                  aria-label={`${t(occ ? 'calendario.disp.cella_occupata' : 'calendario.disp.cella', { giorno: giornoBreve(i, l), fascia: nomeFascia(fascia).toLowerCase() })}${ecc ? `: ${t('calendario.disp.eccezione_del', { giorno: daIso(giorno).getDate() })}` : ''}${passato ? `: ${t('calendario.disp.passato')}` : ''}`}
                  title={occ ? t('calendario.disp.visita_fissata')
                    : ecc ? t('calendario.disp.eccezione_titolo', { data: `${daIso(giorno).getDate()} ${mese(daIso(giorno), l)}` })
                      : passato ? t('calendario.disp.giorno_passato') : undefined} />
              );
            })}
          </React.Fragment>
        ))}
      </div>
      <div className="dfoot">
        <button type="button" className={`sw${sempre ? ' on' : ''}`} onClick={ogniSettimana} aria-pressed={sempre}>
          <i />{t('calendario.disp.ogni_settimana')}
        </button>
        <span>{mai.length === 7 ? t(dello_staff ? 'calendario.disp.nessuna_staff' : 'calendario.disp.toccate')
          : mai.length ? t('calendario.disp.mai_giorni', { giorni: mai.join(', ') }) : t('calendario.disp.tutti_i_giorni')}</span>
      </div>
      <div className="eccezioni">
        <span className="muto">{t('calendario.disp.eccezioni')}</span>
        {eccezioni.map((g) => (
          <span key={g} className="ecc">{daIso(g).getDate()} {meseBreve(daIso(g), l)} · {testoEccezione(g)}<button type="button" onClick={() => togliEccezione(g)} aria-label={t('calendario.disp.togli_eccezione', { giorno: daIso(g).getDate() })}>✕</button></span>
        ))}
        <input type="date" value={nuovaEccezione} min={oggi} onChange={(e) => setNuovaEccezione(e.target.value)} aria-label={t('calendario.disp.giorno_non_potete')}
          style={{ border: '1px solid var(--line2)', borderRadius: 10, padding: '.25rem .5rem', minHeight: 32 }} />
        <button type="button" className="btn xs" onClick={aggiungiEccezione} disabled={!nuovaEccezione}>{t('calendario.disp.quel_giorno_no')}</button>
      </div>
      {/* il giorno dell'aggiornamento nell'ora di Roma (aggiornata_alle è in UTC) */}
      {testoAggiornata(disponibilitaGiornoDiRoma(disp), dello_staff, l) && (
        <p className="aggiornata" data-testid="aggiornata">{testoAggiornata(disponibilitaGiornoDiRoma(disp), dello_staff, l)}</p>
      )}
    </div>
  );
};

export default Disponibilita;
